import { niceNum } from '@/core';

const SPLIT_NUMBER = 5;

export type FindNiceMinMaxArgs = {
  /**
   * Minimum data value used for calculating the optimal min
   */
  minValue: number;
  /**
   * Maximum data value used for calculating the optimal max
   */
  maxValue: number;
  /**
   * Hard minimum value that overrides the calculated min
   */
  hardMin?: number;
  /**
   * Hard maximum value that overrides the calculated max
   */
  hardMax?: number;
  /**
   * Amount of padding to add to the min / max as a ratio of the total range
   */
  paddingFactor?: {
    /**
     * Ratio used to decrease the calculated min
     */
    min: number;
    /**
     * Ratio used to increase the calculated max
     */
    max: number;
  };
};

/**
 * This function finds nice min/max values that we can pass on to ECharts
 * so that the chart has nicely formatted ticklines.
 */
export function findNiceMinMax({
  minValue,
  maxValue,
  hardMin,
  hardMax,
  paddingFactor = { min: 0.1, max: 0.05 },
}: FindNiceMinMaxArgs) {
  // No need for calculation if we have defined min and max
  if (hardMin !== undefined && hardMax !== undefined) {
    return [hardMin, hardMax];
  }

  // Use defined min/max or given min/max values
  let min = hardMin !== undefined ? hardMin : minValue;
  let max = hardMax !== undefined ? hardMax : maxValue;

  if (min === max) {
    if (min < 0) {
      max = 0;
    } else if (min > 0) {
      min = 0;
    } else {
      // Flat 0 will have 0 - 100 range
      max = 100;
    }
  }

  const range = max - min;

  // Add slight padding above max
  if (hardMax === undefined) {
    max += range * paddingFactor.max;
  }

  const interval = niceNum(range / SPLIT_NUMBER);
  const precision = getPrecisionSafe(interval);

  // For medium size integer ranges (>=1 and <=10), prefer integer tick intervals
  if (
    shouldUseIntegerBounds({
      range,
      initialInterval: interval,
      originalMin: minValue,
      originalMax: maxValue,
      hardMin,
      hardMax,
    })
  ) {
    return calculateIntegerBounds({ minValue, maxValue, paddingFactor });
  }

  // Find a nice tickline just above and just below min/max
  let tickMin = precisionRound(Math.floor(min / interval) * interval, precision);
  const tickMax = precisionRound(Math.ceil(max / interval) * interval, precision);

  // Add slight padding below min, but do not go into negatives unnecessarily.
  // Must happen after precisionRound is called so we can tell whether zero was the suggested min.
  if (hardMin === undefined && tickMin !== 0) {
    const minWithPadding = tickMin - range * paddingFactor.min;
    tickMin = precisionRound(Math.floor(minWithPadding / interval) * interval, precision);
  }

  // Get tick interval after rounding
  const tickInterval = niceNum((tickMax - tickMin) / SPLIT_NUMBER);

  // If the slight rounding below and above the min/max causes a change in tickInterval
  // then we need to use that new tickInterval to make sure we have nice ticklines
  if (interval !== tickInterval) {
    return [
      precisionRound(Math.floor(tickMin / tickInterval) * tickInterval, precision),
      precisionRound(Math.ceil(tickMax / tickInterval) * tickInterval, precision),
    ];
  }

  return [tickMin, tickMax];
}

/**
 * Determines whether to use prefer integer bounds calculation.
 */
export function shouldUseIntegerBounds({
  range,
  initialInterval,
  originalMin,
  originalMax,
  hardMin,
  hardMax,
}: {
  range: number;
  initialInterval: number;
  originalMin: number;
  originalMax: number;
  hardMin?: number;
  hardMax?: number;
}): boolean {
  if (hardMin !== undefined || hardMax !== undefined) {
    return false;
  }
  const isMediumSizeRange = range >= 1 && range <= 10;
  const isIntegerData = Number.isInteger(originalMin) && Number.isInteger(originalMax);
  return isIntegerData && isMediumSizeRange && initialInterval < 1;
}

/**
 * Calculates bounds for small integer ranges to ensure integer tick marks
 */
export function calculateIntegerBounds({
  minValue,
  maxValue,
  paddingFactor,
}: {
  minValue: number;
  maxValue: number;
  paddingFactor: { min: number; max: number };
}): [number, number] {
  const range = maxValue - minValue;
  // Ensure at least 1 unit of padding for integer ranges
  const minPadding = Math.max(1, Math.ceil(range * paddingFactor.min));
  const maxPadding = Math.max(1, Math.ceil(range * paddingFactor.max));

  // Add padding to the min and keep zero as the min if data starts at zero
  const paddedMin = minValue === 0 ? 0 : minValue - minPadding;

  // Add padding to the max
  const paddedMax = maxValue + maxPadding;

  return [paddedMin, paddedMax];
}

// Return the precision of the given number
function getPrecisionSafe(val: number) {
  const str = val.toString().toLowerCase(); // Consider scientific notation: '3.4e-12' '3.4e+12'

  const eIndex = str.indexOf('e');
  const exp = eIndex > 0 ? +str.slice(eIndex + 1) : 0;
  const significandPartLen = eIndex > 0 ? eIndex : str.length;
  const dotIndex = str.indexOf('.');
  const decimalPartLen = dotIndex < 0 ? 0 : significandPartLen - 1 - dotIndex;
  return Math.max(0, decimalPartLen - exp);
}

const ROUND_SUPPORTED_PRECISION_MAX = 20;
// Round the number to the given precision
// ie 1.235 rounded to precision 2 is 1.24
//    1.234 rounded to precision 2 is 1.23
function precisionRound(val: number, precision: number) {
  if (precision == null) {
    precision = 10;
  } // Avoid range error

  precision = Math.min(Math.max(0, precision), ROUND_SUPPORTED_PRECISION_MAX);

  return +(+val).toFixed(precision);
}
